import type { ContractsListResponse, ContractsQueryParams } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import {
  zodContractsListResponseSchema,
  zodContractsQueryParamsSchema,
} from './validations';

/**
 * Fetches all contracts from the API with the given query parameters.
 *
 * @param queryParams - The query parameters for fetching contracts.
 * @param options - Additional request options.
 * @returns The paginated contract data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/list_contracts_contracts_get
 */
export const fetchContracts = async (
  queryParams: Partial<ContractsQueryParams>,
  options: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT.GET_ALL(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodContractsQueryParamsSchema
          .transform(({ limit, offset, page, ...rest }) => {
            // Translate page value to offset and remove 'page' key
            return {
              ...rest,
              limit,
              offset: page ? page * limit : offset,
            };
          })
          .transform((data) =>
            withoutObjectKeysWhereValueIs(data, [undefined, '']),
          ),
      },
      ...options,
    })
    .json<ContractsListResponse | undefined>();

  // Validate the response with Zod
  const validatedResponse = zodContractsListResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Contracts API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
